import { useCallback, useMemo } from 'react';
import { Clinic } from '../../models/MedVox/Clinic';
import { FilterOption, useFilterOptions } from './useFilterOptions';

export const CLINIC_SELECT_ALL_LABEL = 'Все';
export const CLINIC_SELECT_ALL_VALUE = '__all__';

const buildClinicOption = (clinic: Clinic): FilterOption => ({
  id: clinic.id,
  label: clinic.name,
  value: clinic.id,
});

export const stripClinicSelectAllValue = (clinicIds: string[]) =>
  clinicIds.filter((clinicId) => clinicId !== CLINIC_SELECT_ALL_VALUE);

export type SelectAllBehavior = 'clear-filter' | 'select-all';

const areAllClinicsSelected = (
  selectedClinicIds: string[],
  availableClinicIds: string[],
) =>
  availableClinicIds.length > 0 &&
  availableClinicIds.every((clinicId) => selectedClinicIds.includes(clinicId));

export const normalizeClinicSelectAllValue = (
  nextSelectedClinicIds: string[],
  selectedClinicIds: string[],
  availableClinicIds: string[],
  selectAllBehavior: SelectAllBehavior = 'clear-filter',
) => {
  if (!nextSelectedClinicIds.includes(CLINIC_SELECT_ALL_VALUE)) {
    return stripClinicSelectAllValue(nextSelectedClinicIds);
  }

  if (selectAllBehavior === 'clear-filter') {
    return selectedClinicIds.length === 0
      ? stripClinicSelectAllValue(nextSelectedClinicIds)
      : [];
  }

  if (areAllClinicsSelected(selectedClinicIds, availableClinicIds)) {
    return [];
  }

  return availableClinicIds;
};

export const useClinicSelectAllOptions = (
  clinics: Clinic[],
  selectedClinicIds: string[],
  selectAllBehavior: SelectAllBehavior = 'clear-filter',
) => {
  const clinicOptions = useFilterOptions(
    clinics,
    selectedClinicIds,
    (clinic) => selectedClinicIds.includes(clinic.id),
    buildClinicOption,
  );

  const availableClinicIds = useMemo(
    () => clinics.map((clinic) => clinic.id),
    [clinics],
  );

  const options = useMemo(
    () => [
      { label: CLINIC_SELECT_ALL_LABEL, value: CLINIC_SELECT_ALL_VALUE },
      ...clinicOptions,
    ],
    [clinicOptions],
  );

  const displaySelectedClinicIds = useMemo(
    () =>
      selectAllBehavior === 'clear-filter' && selectedClinicIds.length === 0
        ? [CLINIC_SELECT_ALL_VALUE]
        : selectedClinicIds,
    [selectAllBehavior, selectedClinicIds],
  );

  const normalizeSelectedClinicIds = useCallback(
    (nextSelectedClinicIds: string[]) =>
      normalizeClinicSelectAllValue(
        nextSelectedClinicIds,
        selectedClinicIds,
        availableClinicIds,
        selectAllBehavior,
      ),
    [availableClinicIds, selectedClinicIds, selectAllBehavior],
  );

  return {
    options,
    displaySelectedClinicIds,
    normalizeSelectedClinicIds,
  };
};
